import { useAppLanguage } from '@/hooks/useAppLanguage';
import Colors from '@/constants/colors';
import DetailPageLayout from '@/components/DetailPageLayout';
import DetailPageHeader from '@/components/DetailPageHeader';

const content = {
  en: {
    title: 'Terms of Service',
    lastUpdated: 'Last updated: December 2024',
    acceptance: {
      title: 'Acceptance of Terms',
      text: 'By accessing and using Umland, you accept and agree to be bound by these Terms of Service. If you do not agree with any part of these terms, please do not use the application.',
    },
    description: {
      title: 'Service Description',
      text: 'Umland is a free informational application designed to help expats, newcomers, and visitors navigate life in Iceland. The app provides:',
      items: [
        'Articles with practical advice and information',
        'Information about local places and services',
        'Event listings and activities',
        'Utility widgets (weather, currency rates, holidays, etc.)',
      ],
    },
    userConduct: {
      title: 'User Conduct',
      text: 'When using Umland, you agree to:',
      items: [
        'Use the app for lawful purposes only',
        'Not attempt to disrupt or interfere with the app\'s functionality',
        'Not attempt to access data or systems you are not authorized to access',
        'Respect the intellectual property rights of the app and its content',
      ],
    },
    disclaimer: {
      title: 'Disclaimer',
      text: 'The information provided in Umland is for general informational purposes only. While we strive to provide accurate and up-to-date information:',
      items: [
        'Content is not intended as legal, financial, or official advice',
        'Information may become outdated or change without notice',
        'Always verify important information with official sources',
        'Consult professionals for specific legal, medical, or financial matters',
      ],
      note: 'Umland is not affiliated with any government agency or official institution in Iceland.',
    },
    accuracy: {
      title: 'Content Accuracy',
      text: 'We make reasonable efforts to ensure the accuracy of information in the app. However, we do not guarantee that all information is complete, accurate, or current. If you find any errors or outdated information, please report it to us.',
    },
    intellectualProperty: {
      title: 'Intellectual Property',
      text: 'All content in Umland, including but not limited to text, graphics, logos, and software, is the property of Umland or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without permission.',
    },
    thirdParty: {
      title: 'Third-Party Links and Content',
      text: 'The app may contain links to third-party websites or services. We are not responsible for the content, privacy policies, or practices of any third-party sites. Use of external links is at your own risk.',
    },
    liability: {
      title: 'Limitation of Liability',
      text: 'To the fullest extent permitted by law, Umland and its creators shall not be liable for any:',
      items: [
        'Direct, indirect, incidental, or consequential damages',
        'Loss of data, profits, or business opportunities',
        'Damages arising from reliance on app content',
        'Issues caused by app unavailability or technical problems',
      ],
      note: 'The app is provided "as is" without warranties of any kind, either express or implied.',
    },
    modifications: {
      title: 'Modifications to Terms',
      text: 'We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Your continued use of the app after changes constitutes acceptance of the modified terms.',
    },
    termination: {
      title: 'Termination',
      text: 'We may suspend or terminate access to the app at any time, without prior notice, for any reason. All provisions of these terms which should survive termination shall survive.',
    },
    governingLaw: {
      title: 'Governing Law',
      text: 'These terms shall be governed by and construed in accordance with the laws of Iceland, without regard to conflict of law principles.',
    },
    contact: {
      title: 'Contact Us',
      text: 'If you have any questions about these Terms of Service, please contact us at:',
      email: 'support@umland.is',
    },
  },
  ru: {
    title: 'Условия использования',
    lastUpdated: 'Последнее обновление: декабрь 2024',
    acceptance: {
      title: 'Принятие условий',
      text: 'Получая доступ к Umland и используя его, вы принимаете и соглашаетесь соблюдать настоящие Условия использования. Если вы не согласны с какой-либо частью этих условий, пожалуйста, не используйте приложение.',
    },
    description: {
      title: 'Описание сервиса',
      text: 'Umland - это бесплатное информационное приложение, созданное для помощи экспатам, новоприбывшим и посетителям в жизни в Исландии. Приложение предоставляет:',
      items: [
        'Статьи с практическими советами и информацией',
        'Информацию о местных местах и сервисах',
        'Списки событий и мероприятий',
        'Полезные виджеты (погода, курсы валют, праздники и т.д.)',
      ],
    },
    userConduct: {
      title: 'Поведение пользователя',
      text: 'Используя Umland, вы соглашаетесь:',
      items: [
        'Использовать приложение только в законных целях',
        'Не пытаться нарушить или вмешиваться в функциональность приложения',
        'Не пытаться получить доступ к данным или системам без авторизации',
        'Уважать права интеллектуальной собственности приложения и его контента',
      ],
    },
    disclaimer: {
      title: 'Отказ от ответственности',
      text: 'Информация в Umland предоставляется только в общих информационных целях. Хотя мы стремимся предоставлять точную и актуальную информацию:',
      items: [
        'Контент не является юридической, финансовой или официальной консультацией',
        'Информация может устареть или измениться без предупреждения',
        'Всегда проверяйте важную информацию в официальных источниках',
        'Консультируйтесь со специалистами по юридическим, медицинским или финансовым вопросам',
      ],
      note: 'Umland не связан с какими-либо государственными органами или официальными учреждениями Исландии.',
    },
    accuracy: {
      title: 'Точность контента',
      text: 'Мы прилагаем разумные усилия для обеспечения точности информации в приложении. Однако мы не гарантируем, что вся информация является полной, точной или актуальной. Если вы обнаружите ошибки или устаревшую информацию, пожалуйста, сообщите нам.',
    },
    intellectualProperty: {
      title: 'Интеллектуальная собственность',
      text: 'Весь контент в Umland, включая, но не ограничиваясь текстами, графикой, логотипами и программным обеспечением, является собственностью Umland или его поставщиков контента и защищен законами об интеллектуальной собственности. Вы не можете воспроизводить, распространять или создавать производные работы без разрешения.',
    },
    thirdParty: {
      title: 'Ссылки и контент третьих сторон',
      text: 'Приложение может содержать ссылки на сторонние веб-сайты или сервисы. Мы не несем ответственности за контент, политику конфиденциальности или практики сторонних сайтов. Использование внешних ссылок осуществляется на ваш риск.',
    },
    liability: {
      title: 'Ограничение ответственности',
      text: 'В максимальной степени, разрешенной законом, Umland и его создатели не несут ответственности за:',
      items: [
        'Прямые, косвенные, случайные или последующие убытки',
        'Потерю данных, прибыли или деловых возможностей',
        'Ущерб от использования контента приложения',
        'Проблемы, вызванные недоступностью приложения или техническими неполадками',
      ],
      note: 'Приложение предоставляется "как есть" без каких-либо гарантий, явных или подразумеваемых.',
    },
    modifications: {
      title: 'Изменение условий',
      text: 'Мы оставляем за собой право изменять эти условия в любое время. Изменения вступают в силу сразу после публикации. Продолжение использования приложения после изменений означает принятие измененных условий.',
    },
    termination: {
      title: 'Прекращение',
      text: 'Мы можем приостановить или прекратить доступ к приложению в любое время без предварительного уведомления по любой причине. Все положения этих условий, которые должны сохраняться после прекращения, остаются в силе.',
    },
    governingLaw: {
      title: 'Применимое право',
      text: 'Настоящие условия регулируются и толкуются в соответствии с законодательством Исландии без учета коллизионных норм.',
    },
    contact: {
      title: 'Связаться с нами',
      text: 'Если у вас есть вопросы об этих Условиях использования, пожалуйста, свяжитесь с нами:',
      email: 'support@umland.is',
    },
  },
  pl: {
    title: 'Warunki użytkowania',
    lastUpdated: 'Ostatnia aktualizacja: grudzień 2024',
    acceptance: {
      title: 'Akceptacja warunków',
      text: 'Uzyskując dostęp do Umland i korzystając z niego, akceptujesz i zgadzasz się przestrzegać niniejszych Warunków użytkowania. Jeśli nie zgadzasz się z jakąkolwiek częścią tych warunków, prosimy nie korzystać z aplikacji.',
    },
    description: {
      title: 'Opis usługi',
      text: 'Umland to bezpłatna aplikacja informacyjna zaprojektowana, aby pomóc ekspatom, nowo przybyłym i odwiedzającym w życiu na Islandii. Aplikacja zapewnia:',
      items: [
        'Artykuły z praktycznymi poradami i informacjami',
        'Informacje o lokalnych miejscach i usługach',
        'Listy wydarzeń i aktywności',
        'Użyteczne widżety (pogoda, kursy walut, święta itp.)',
      ],
    },
    userConduct: {
      title: 'Zachowanie użytkownika',
      text: 'Korzystając z Umland, zgadzasz się:',
      items: [
        'Używać aplikacji tylko do zgodnych z prawem celów',
        'Nie próbować zakłócać ani ingerować w funkcjonalność aplikacji',
        'Nie próbować uzyskać dostępu do danych lub systemów bez autoryzacji',
        'Szanować prawa własności intelektualnej aplikacji i jej treści',
      ],
    },
    disclaimer: {
      title: 'Zastrzeżenie',
      text: 'Informacje w Umland są udostępniane wyłącznie w ogólnych celach informacyjnych. Choć staramy się dostarczać dokładne i aktualne informacje:',
      items: [
        'Treść nie stanowi porady prawnej, finansowej ani oficjalnej',
        'Informacje mogą stać się nieaktualne lub ulec zmianie bez powiadomienia',
        'Zawsze weryfikuj ważne informacje w oficjalnych źródłach',
        'Skonsultuj się ze specjalistami w sprawach prawnych, medycznych lub finansowych',
      ],
      note: 'Umland nie jest powiązany z żadną agencją rządową ani oficjalną instytucją na Islandii.',
    },
    accuracy: {
      title: 'Dokładność treści',
      text: 'Dokładamy uzasadnionych starań, aby zapewnić dokładność informacji w aplikacji. Jednak nie gwarantujemy, że wszystkie informacje są kompletne, dokładne lub aktualne. Jeśli znajdziesz błędy lub nieaktualne informacje, prosimy o zgłoszenie.',
    },
    intellectualProperty: {
      title: 'Własność intelektualna',
      text: 'Cała zawartość Umland, w tym między innymi teksty, grafika, logo i oprogramowanie, jest własnością Umland lub jej dostawców treści i jest chroniona prawami własności intelektualnej. Nie możesz reprodukować, dystrybuować ani tworzyć dzieł pochodnych bez pozwolenia.',
    },
    thirdParty: {
      title: 'Linki i treści stron trzecich',
      text: 'Aplikacja może zawierać linki do stron internetowych lub usług stron trzecich. Nie ponosimy odpowiedzialności za treść, polityki prywatności ani praktyki stron trzecich. Korzystanie z zewnętrznych linków odbywa się na własne ryzyko.',
    },
    liability: {
      title: 'Ograniczenie odpowiedzialności',
      text: 'W najszerszym zakresie dozwolonym przez prawo, Umland i jego twórcy nie ponoszą odpowiedzialności za:',
      items: [
        'Bezpośrednie, pośrednie, przypadkowe lub wynikowe szkody',
        'Utratę danych, zysków lub możliwości biznesowych',
        'Szkody wynikające z polegania na treści aplikacji',
        'Problemy spowodowane niedostępnością aplikacji lub problemami technicznymi',
      ],
      note: 'Aplikacja jest dostarczana "tak jak jest" bez jakichkolwiek gwarancji, wyraźnych lub dorozumianych.',
    },
    modifications: {
      title: 'Zmiany warunków',
      text: 'Zastrzegamy sobie prawo do zmiany tych warunków w dowolnym momencie. Zmiany wchodzą w życie natychmiast po opublikowaniu. Dalsze korzystanie z aplikacji po zmianach oznacza akceptację zmienionych warunków.',
    },
    termination: {
      title: 'Zakończenie',
      text: 'Możemy zawiesić lub zakończyć dostęp do aplikacji w dowolnym momencie, bez wcześniejszego powiadomienia, z dowolnego powodu. Wszystkie postanowienia tych warunków, które powinny przetrwać zakończenie, pozostają w mocy.',
    },
    governingLaw: {
      title: 'Prawo właściwe',
      text: 'Niniejsze warunki podlegają i są interpretowane zgodnie z prawem Islandii, bez względu na zasady kolizji praw.',
    },
    contact: {
      title: 'Skontaktuj się z nami',
      text: 'Jeśli masz pytania dotyczące niniejszych Warunków użytkowania, skontaktuj się z nami:',
      email: 'support@umland.is',
    },
  },
};

export default function TermsOfServicePage() {
  const { language } = useAppLanguage();
  const t = content[language] || content.en;

  return (
    <DetailPageLayout>
      <DetailPageHeader title={t.title} />
      <div style={styles.content}>
        <p style={styles.lastUpdated}>{t.lastUpdated}</p>

        {/* Acceptance */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.acceptance.title}</h2>
          <p style={styles.bodyText}>{t.acceptance.text}</p>
        </section>

        {/* Description */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.description.title}</h2>
          <p style={styles.bodyText}>{t.description.text}</p>
          <ul style={styles.list}>
            {t.description.items.map((item, index) => (
              <li key={index} style={styles.listItem}>{item}</li>
            ))}
          </ul>
        </section>

        {/* User Conduct */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.userConduct.title}</h2>
          <p style={styles.bodyText}>{t.userConduct.text}</p>
          <ul style={styles.list}>
            {t.userConduct.items.map((item, index) => (
              <li key={index} style={styles.listItem}>{item}</li>
            ))}
          </ul>
        </section>

        {/* Disclaimer */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.disclaimer.title}</h2>
          <p style={styles.bodyText}>{t.disclaimer.text}</p>
          <ul style={styles.list}>
            {t.disclaimer.items.map((item, index) => (
              <li key={index} style={styles.listItem}>{item}</li>
            ))}
          </ul>
          <p style={styles.note}>{t.disclaimer.note}</p>
        </section>

        {/* Accuracy */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.accuracy.title}</h2>
          <p style={styles.bodyText}>{t.accuracy.text}</p>
        </section>

        {/* Intellectual Property */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.intellectualProperty.title}</h2>
          <p style={styles.bodyText}>{t.intellectualProperty.text}</p>
        </section>

        {/* Third Party */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.thirdParty.title}</h2>
          <p style={styles.bodyText}>{t.thirdParty.text}</p>
        </section>

        {/* Liability */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.liability.title}</h2>
          <p style={styles.bodyText}>{t.liability.text}</p>
          <ul style={styles.list}>
            {t.liability.items.map((item, index) => (
              <li key={index} style={styles.listItem}>{item}</li>
            ))}
          </ul>
          <p style={styles.note}>{t.liability.note}</p>
        </section>

        {/* Modifications */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.modifications.title}</h2>
          <p style={styles.bodyText}>{t.modifications.text}</p>
        </section>

        {/* Termination */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.termination.title}</h2>
          <p style={styles.bodyText}>{t.termination.text}</p>
        </section>

        {/* Governing Law */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.governingLaw.title}</h2>
          <p style={styles.bodyText}>{t.governingLaw.text}</p>
        </section>

        {/* Contact */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>{t.contact.title}</h2>
          <p style={styles.bodyText}>{t.contact.text}</p>
          <a href={`mailto:${t.contact.email}`} style={styles.emailLink}>
            {t.contact.email}
          </a>
        </section>
      </div>
    </DetailPageLayout>
  );
}

const styles = {
  content: {
    backgroundColor: Colors.background,
    minHeight: '100vh',
    padding: '24px',
  } as React.CSSProperties,

  lastUpdated: {
    fontSize: '14px',
    color: Colors.textSecondary,
    marginBottom: '24px',
  } as React.CSSProperties,

  section: {
    marginBottom: '28px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: Colors.text,
    marginBottom: '12px',
  } as React.CSSProperties,

  bodyText: {
    fontSize: '16px',
    color: Colors.text,
    lineHeight: '24px',
    margin: 0,
  } as React.CSSProperties,

  list: {
    margin: '12px 0 0 0',
    paddingLeft: '20px',
  } as React.CSSProperties,

  listItem: {
    fontSize: '16px',
    color: Colors.text,
    lineHeight: '28px',
  } as React.CSSProperties,

  note: {
    fontSize: '14px',
    color: Colors.textSecondary,
    lineHeight: '22px',
    marginTop: '12px',
    fontStyle: 'italic',
  } as React.CSSProperties,

  emailLink: {
    display: 'inline-block',
    marginTop: '8px',
    fontSize: '16px',
    color: Colors.primary,
    textDecoration: 'none',
    fontWeight: '500',
  } as React.CSSProperties,
};
